/**
 * Runtime platform detection + storage adapter selection.
 *
 * The same SPA bundle runs in two shells:
 *   - Standalone web app (https://tkcc.360shop.tech) → window.localStorage.
 *   - Chrome MV3 side-panel page → chrome.storage.local (async).
 *
 * Supabase's GoTrue client needs a storage adapter to persist the session. In
 * the extension we MUST use chrome.storage.local (never chrome.storage.sync —
 * that replicates secrets to Google's cloud). See lib/supabase.ts.
 *
 * v1 is a thin side-panel shell only: NO content scripts, NO live audio, NO
 * background message passing beyond what the side panel itself needs.
 */
import {
  chromeStorageAdapter,
  type AsyncStorageAdapter,
} from "@/lib/chromeStorageAdapter";

/** Compile-time target injected by vite.config.ts (`web` | `extension`). */
declare const __BUILD_TARGET__: "web" | "extension";

/** True when running inside the Chrome extension side-panel context. */
export function isExtension(): boolean {
  // Prefer a real runtime check (the same bundle may be loaded either way),
  // falling back to the compile-time target.
  const hasChromeStorage =
    typeof chrome !== "undefined" &&
    !!chrome?.storage?.local &&
    !!chrome?.runtime?.id;

  if (hasChromeStorage) return true;

  try {
    return __BUILD_TARGET__ === "extension";
  } catch {
    return false;
  }
}

/** The compile-time build target string. */
export function buildTarget(): "web" | "extension" {
  try {
    return __BUILD_TARGET__;
  } catch {
    return "web";
  }
}

/**
 * A localStorage-backed adapter that satisfies the same async interface as the
 * chrome adapter, so consumers (Supabase client) can treat them uniformly.
 */
const localStorageAdapter: AsyncStorageAdapter = {
  async getItem(key) {
    return window.localStorage.getItem(key);
  },
  async setItem(key, value) {
    window.localStorage.setItem(key, value);
  },
  async removeItem(key) {
    window.localStorage.removeItem(key);
  },
};

/**
 * Returns the correct persistent storage adapter for the current shell.
 * Used by the Supabase client (session persistence) and any future
 * platform-agnostic persistence.
 */
export function getStorageAdapter(): AsyncStorageAdapter {
  return isExtension() ? chromeStorageAdapter : localStorageAdapter;
}
